import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ListSectionHeaderProps {
  /** Section title, e.g. "Regions". */
  title: ReactNode;
  /** Optional total shown in the count pill. Hidden when undefined. */
  count?: number;
  /** Right-aligned actions (Select, Create dialog, etc.). */
  children?: ReactNode;
  className?: string;
}

/**
 * Standard section header for the organization tabs (Regions, Locations, Users,
 * Companies, Roles). Renders a compact title + count pill on the left and an
 * actions slot on the right. The big page title lives in the org layout
 * (`OrgHeader`), so each tab only needs this lighter section header.
 */
export function ListSectionHeader({
  title,
  count,
  children,
  className,
}: ListSectionHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between pl-2", className)}>
      <div className="flex items-center gap-3">
        <h2 className="text-[1.75rem] font-semibold leading-none text-(--color-text-primary)">
          {title}
        </h2>
        {count != null && (
          <span className="rounded-full bg-(--shell-border) px-3 py-0.5 text-base font-medium text-(--color-text-primary)">
            {count}
          </span>
        )}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}
